import { useState } from 'react';
import { Package, Truck, Check, Leaf } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const bagTypes = [
  'Jute Bags',
  'Cotton Canvas Bags',
  'Pouches',
  'Wine Bags',
  'Promotional Bags',
  'Custom Design',
];

const quantityRanges = [
  '100 - 500 units',
  '500 - 1,000 units',
  '1,000 - 5,000 units',
  '5,000 - 10,000 units',
  '10,000+ units',
];

const wholesaleBenefits = [
  'Competitive bulk pricing for retailers and brands',
  'Flexible MOQs with options for small and large orders',
  'Custom branding, printing and design support',
  'Export-ready packaging and worldwide shipping',
  'Dedicated account support for wholesale partners',
];

export const WholesalePage = ({ onRequestQuote }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    bagType: '',
    quantity: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch('/php/send-mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'wholesale',
          name: formData.name,
          company: formData.company,
          email: formData.email,
          phone: formData.phone,
          bagType: formData.bagType,
          quantity: formData.quantity,
          message: formData.message,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Something went wrong');
      }
      setIsSubmitted(true);
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        bagType: '',
        quantity: '',
        message: '',
      });
    } catch (err) {
      alert(err.message || 'Failed to send. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F5F2ED] min-h-screen pb-16">
      {/* Hero - text only, no image */}
      <section className="pt-24 md:pt-28 pb-10 md:pb-12 leaf-pattern" id="home">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className=" text-xs tracking-[0.25em] uppercase text-[#5A5A5A] mb-3">
            Bulk Orders & Export
          </p>
          <h1 className=" text-3xl sm:text-4xl lg:text-5xl text-[#2B2B2B] mb-4">
            Wholesale & B2B
          </h1>
          <p className=" text-sm sm:text-base text-[#5A5A5A] max-w-2xl">
            Partner with Bags4Ever for export-quality jute, cotton and canvas bags. We serve
            retailers, brands and promotional agencies worldwide with flexible MOQs and
            custom solutions.
          </p>
        </div>
      </section>

      {/* Manufacturing / production image */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8 md:pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="rounded-xl overflow-hidden border border-[#E3DED8] shadow-md">
            <img
              src={`${process.env.PUBLIC_URL || ''}/manufacturing2.jpeg`}
              alt="Bags4Ever manufacturing floor – production of canvas and tote bags"
              className="w-full h-auto object-cover max-h-[480px] md:max-h-[520px]"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-8 md:py-12 bg-[#F5F2ED]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className=" text-2xl sm:text-3xl text-[#2B2B2B] mb-6">
            Why Partner With Us
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {wholesaleBenefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start gap-3 bg-white/80 rounded-lg border border-[#E3DED8] p-4"
              >
                <div className="w-8 h-8 rounded-full bg-[#2F4F3E]/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-[#2F4F3E]" />
                </div>
                <p className=" text-sm text-[#2B2B2B]">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we offer - with product images */}
      <section className="py-8 md:py-12 border-t border-[#E3DED8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className=" text-2xl sm:text-3xl text-[#2B2B2B] mb-6">
            What We Offer
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col bg-white/80 rounded-lg border border-[#E3DED8] overflow-hidden">
              <div className="flex items-center justify-center aspect-[3/4] rounded-t-lg bg-[#FAFAFA] p-4 overflow-hidden">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/18.png`}
                  alt="Jute and natural fibre bags – product range"
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="p-6 text-center">
                <Package className="w-10 h-10 text-[#2F4F3E] mx-auto mb-3" />
                <h3 className=" text-lg text-[#2B2B2B] mb-2">Product Range</h3>
                <p className=" text-sm text-[#5A5A5A]">
                  Jute bags, cotton canvas bags, pouches, wine bags and custom designs
                  for retail, promotional and corporate use.
                </p>
              </div>
            </div>
            <div className="flex flex-col bg-white/80 rounded-lg border border-[#E3DED8] overflow-hidden">
              <div className="flex items-center justify-center aspect-[3/4] rounded-t-lg bg-[#FAFAFA] p-4 overflow-hidden">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/42.png`}
                  alt="Custom branded canvas totes – eco materials"
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="p-6 text-center">
                <Leaf className="w-10 h-10 text-[#2F4F3E] mx-auto mb-3" />
                <h3 className=" text-lg text-[#2B2B2B] mb-2">Eco Materials</h3>
                <p className=" text-sm text-[#5A5A5A]">
                  Natural jute, organic cotton, recycled fibers and low-impact inks to meet
                  sustainability and compliance requirements.
                </p>
              </div>
            </div>
            <div className="flex flex-col bg-white/80 rounded-lg border border-[#E3DED8] overflow-hidden">
              <div className="flex items-center justify-center aspect-[3/4] rounded-t-lg bg-[#FAFAFA] p-4 overflow-hidden">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/34.png`}
                  alt="Export-ready striped tote – global shipping"
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="p-6 text-center">
                <Truck className="w-10 h-10 text-[#2F4F3E] mx-auto mb-3" />
                <h3 className=" text-lg text-[#2B2B2B] mb-2">Global Export</h3>
                <p className=" text-sm text-[#5A5A5A]">
                  Export-ready packaging, documentation and shipping to Europe, Middle East,
                  Asia and the Americas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wholesale Enquiry Form */}
      <section id="wholesale-enquiry" className="py-12 md:py-16 bg-[#ECE7E1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <h2 className=" text-2xl sm:text-3xl text-[#2B2B2B] mb-2">
              Wholesale Enquiry
            </h2>
            <p className=" text-sm text-[#5A5A5A] mb-8">
              Tell us about your requirements. We&apos;ll respond within 24 hours with
              pricing and options.
            </p>

            {isSubmitted ? (
              <div className="bg-white/90 rounded-lg border border-[#E3DED8] p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-[#2F4F3E]/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-7 h-7 text-[#2F4F3E]" />
                </div>
                <h3 className=" text-xl text-[#2B2B2B] mb-2">Thank You</h3>
                <p className=" text-sm text-[#5A5A5A] mb-6">
                  We&apos;ve received your enquiry and will get back to you shortly.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className=" text-sm"
                  onClick={() => setIsSubmitted(false)}
                >
                  Submit Another Enquiry
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white/90 rounded-lg border border-[#E3DED8] p-5 sm:p-6 shadow-sm space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="wholesale-name" className=" text-sm text-[#2B2B2B]">
                      Full Name *
                    </Label>
                    <Input
                      id="wholesale-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="wholesale-company" className=" text-sm text-[#2B2B2B]">
                      Company / Brand *
                    </Label>
                    <Input
                      id="wholesale-company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      placeholder="Your Company"
                      className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="wholesale-email" className=" text-sm text-[#2B2B2B]">
                      Email *
                    </Label>
                    <Input
                      id="wholesale-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                      className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="wholesale-phone" className=" text-sm text-[#2B2B2B]">
                      Phone
                    </Label>
                    <Input
                      id="wholesale-phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className=" text-sm text-[#2B2B2B]">Bag Type *</Label>
                    <Select
                      value={formData.bagType}
                      onValueChange={(value) => handleSelectChange('bagType', value)}
                      required
                    >
                      <SelectTrigger className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#E3DED8]">
                        {bagTypes.map((type) => (
                          <SelectItem key={type} value={type} className=" text-sm">
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className=" text-sm text-[#2B2B2B]">
                      Estimated Quantity
                    </Label>
                    <Select
                      value={formData.quantity}
                      onValueChange={(value) => handleSelectChange('quantity', value)}
                    >
                      <SelectTrigger className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md text-sm">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#E3DED8]">
                        {quantityRanges.map((range) => (
                          <SelectItem key={range} value={range} className=" text-sm">
                            {range}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="wholesale-message" className=" text-sm text-[#2B2B2B]">
                    Requirements / Message
                  </Label>
                  <Textarea
                    id="wholesale-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project, timeline, markets and any customisation needs..."
                    rows={4}
                    className="bg-white border-[#E3DED8] focus:border-[#2F4F3E] focus:ring-[#2F4F3E]/20 rounded-md resize-none text-sm"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#2F4F3E] hover:bg-[#233B2F] text-white rounded-md py-5 text-sm font-medium"
                >
                  {isSubmitting ? 'Sending...' : 'Submit Enquiry'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 border-t border-[#E3DED8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#5A5A5A] text-sm mb-4">
            Prefer to talk? Reach out for a quick quote or sample request.
          </p>
          <Button
            type="button"
            onClick={onRequestQuote}
            className="bg-[#2F4F3E] hover:bg-[#233B2F] text-white rounded-md px-8 py-4 text-sm font-medium"
          >
            Request Quote (Modal)
          </Button>
        </div>
      </section>
    </div>
  );
};
